import React, { useState, useEffect, useCallback } from "react";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Card,
  Button,
  Grid,
  Stack
} from '@mui/material';
import { Close as CloseIcon, Search, Refresh } from '@mui/icons-material';
import CustomTooltip from '../../../components/CustomTooltip';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { sendDataWithPost } from '../../../services/services';
import { showErrorMsg, showSuccessMsg } from '../../../utils/toast';
import moment from 'moment';
import { COLORS } from '../../../utils/colors';
import { styles } from '../../../utils/styles';
import { INCOMING_PENDING_FILTER_START_DATE, EXCEPTION_ACTION_DEFAULT, EXCEPTION_SOURCE } from '../../../utils/constants';
import ManualMatchDialog from './ManualMatchDialog';
import RemarkDialog from './RemarkDialog';
import ExceptionHandlingDialog from '../../../components/finance-enhancement/ExceptionHandlingDialog';
import { APP_USER_SESSION } from '../../../constant/AppConstants';
import * as StorageUtils from '../../../utils/storageUtils';
import CircularLoader from '../../../components/circularLoader';
import ButtonCustom from '../../../components/ButtonCustom';
import ShowDetail from '../../../components/ShowDetail';

const getDefaultFundInDate = () => {
  const startDate = moment(INCOMING_PENDING_FILTER_START_DATE);
  const today = moment();
  const defaultDate = today.clone().subtract(1, 'day');
  while (defaultDate.day() === 0 || defaultDate.day() === 6) {
    defaultDate.subtract(1, 'day');
  }
  return defaultDate.isBefore(startDate) ? startDate : defaultDate;
};

const Incoming = ({ userRole }) => {
  const remarkRole = userRole || [];
  const hasRemarkEdit = remarkRole.includes('Edit');
  const hasRemarkAdd = remarkRole.includes('Add');
  const userRoleFromSession = StorageUtils.getParsedValue(APP_USER_SESSION).role;
  const exceptionHandlingRole = userRoleFromSession.exception_handling || [];
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fundInDate, setFundInDate] = useState(() => getDefaultFundInDate());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedDetailData, setSelectedDetailData] = useState(null);
  const [detailType, setDetailType] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);

  const [manualMatchLoading, setManualMatchLoading] = useState({});
  const [openManualMatchDialog, setOpenManualMatchDialog] = useState(false);
  const [selectedRowForMatch, setSelectedRowForMatch] = useState(null);

  const [openRemarkDialog, setOpenRemarkDialog] = useState(false);
  const [selectedRowForRemark, setSelectedRowForRemark] = useState(null);
  const [isEditRemark, setIsEditRemark] = useState(false);

  const [openExceptionDialog, setOpenExceptionDialog] = useState(false);
  const [selectedRowForException, setSelectedRowForException] = useState(null);
  const [exceptionRemarks, setExceptionRemarks] = useState('');
  const [exceptionAction, setExceptionAction] = useState(EXCEPTION_ACTION_DEFAULT);
  const [exceptionPushLoading, setExceptionPushLoading] = useState({});

  const [retrieveBaseRateLoading, setRetrieveBaseRateLoading] = useState({});
  const [openConfirmRetrieveDialog, setOpenConfirmRetrieveDialog] = useState(false);
  const [selectedRowForRetrieve, setSelectedRowForRetrieve] = useState(null);

  const loadSummaryData = useCallback(async () => {
    setLoading(true);
    
    const handleSuccess = (response) => {
      if (response.data) {
        setSummaryData(response.data);
      } else {
        setSummaryData([]);
      }
      setLoading(false);
    };

    const handleError = (error) => {
      showErrorMsg('Failed to load summary data');
      setLoading(false);
    };

    const filters = {
      fund_in_date: moment(fundInDate).format('YYYY-MM-DD')
    };

    sendDataWithPost(
      '/payout/ingestor/incoming_summary/',
      filters,
      null,
      null,
      handleSuccess,
      handleError
    );
  }, [fundInDate]);

  useEffect(() => {
    loadSummaryData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    loadSummaryData();
  };

  const handleReset = () => {
    setFundInDate(getDefaultFundInDate());
    setPage(0);
  };

  const loadDetailData = async (acquirer, type, acquirerDisplayName, currency) => {
    return new Promise((resolve, reject) => {
    const filters = {
      fund_in_date: moment(fundInDate).format('YYYY-MM-DD'),
      acquirer: acquirer,
      detail_type: type,
      acquirer_display_name: acquirerDisplayName || null,
      currency: currency || null
    };

      sendDataWithPost(
        '/payout/ingestor/incoming_detail/',
        filters,
        null,
        null,
        (response) => {
          resolve(response.data || []);
        },
        (error) => {
          reject(error);
        }
      );
    });
  };

  const handleDetailClick = async (acquirer, type, acquirerDisplayName, currency) => {
    setDetailType(type);
    setDetailLoading(true);
    setOpenDetailDialog(true);
    
    try {
      const detailData = await loadDetailData(acquirer, type, acquirerDisplayName, currency);
      setSelectedDetailData(detailData);
      setDetailLoading(false);
    } catch (error) {
      showErrorMsg('Failed to load detail data');
      setDetailLoading(false);
    }
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedDetailData(null);
    setDetailType('');
  };

  const formatAmount = (amount) => {
    const value = Number.parseFloat(amount || 0);
    return Number.isNaN(value)
      ? '0.00'
      : value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
  };

  const amountToCompare = (amount) => {
    const value = Number.parseFloat(amount || 0);
    return Number.isNaN(value) ? 0 : Number(value.toFixed(2));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const renderAmountAndCount = ({ amount, count, onClick, color }) => {
    const amountValue = formatAmount(amount);
    return (
      <Typography
        variant="body2"
        align="right"
        sx={{ color, whiteSpace: 'nowrap' }}
      >
        {count > 0 && onClick ? (
          <Box component="span">
            (
            <Box
              component="span"
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: color || '#1976d2'
              }}
              onClick={onClick}
            >
              {count}
            </Box>
            )
          </Box>
        ) : (
          <Box component="span">({count})</Box>
        )}
        {' '}{amountValue}
      </Typography>
    );
  };

  const handleManualMatch = (row) => {
    setSelectedRowForMatch(row);
    setOpenManualMatchDialog(true);
  };

  const handleManualMatchConfirm = async (reason) => {
    if (!selectedRowForMatch) {
      showErrorMsg('No row selected for manual matching');
      return;
    }

    const recordId = selectedRowForMatch.id;
    setManualMatchLoading(prev => ({ ...prev, [recordId]: true }));

    const userSession = StorageUtils.getParsedValue(APP_USER_SESSION);
    const userEmail = userSession.user.email;
    
    await sendDataWithPost(
      '/payout/ingestor/manual_match/',
      {
        transaction_id: selectedRowForMatch.acquirer_transaction_id,
        acquirer: selectedRowForMatch.acquirer,
        email: userEmail,
        reason: reason.trim()
      },
      async (response) => {
        // Success: Show success message and refresh both summary and detail data
        showSuccessMsg('Manual match completed successfully');
        
        // Refresh summary data
        loadSummaryData();
        
        try {
          const updatedDetailData = await loadDetailData(selectedRowForMatch.acquirer, detailType, selectedRowForMatch.acquirer_display_name, selectedRowForMatch.acquirer_transaction_currency);
          setSelectedDetailData(updatedDetailData);
        } catch (error) {
          console.error('Failed to refresh detail data:', error);
        }
        
        setManualMatchLoading(prev => ({ ...prev, [recordId]: false }));
        setOpenManualMatchDialog(false);
        setSelectedRowForMatch(null);
      },
      () => {
        // Error callback - just reset loading state
        // sendDataWithPost already handles showing error messages
        setManualMatchLoading(prev => ({ ...prev, [recordId]: false }));
      }
    );
  };

  const handleManualMatchClose = () => {
    setOpenManualMatchDialog(false);
    setSelectedRowForMatch(null);
  };

  const handleAddRemark = (row) => {
    setSelectedRowForRemark({ ...row, fund_in_date: moment(fundInDate).format('YYYY-MM-DD') });
    setIsEditRemark(false);
    setOpenRemarkDialog(true);
  };

  const handleEditRemark = (row) => {
    setSelectedRowForRemark({ ...row, fund_in_date: moment(fundInDate).format('YYYY-MM-DD') });
    setIsEditRemark(true);
    setOpenRemarkDialog(true);
  };

  const handleRemarkDialogClose = () => {
    setOpenRemarkDialog(false);
    setSelectedRowForRemark(null);
  };

  const handleOpenExceptionDialog = (row) => {
    setSelectedRowForException(row);
    setExceptionRemarks('');
    setExceptionAction(EXCEPTION_ACTION_DEFAULT);
    setOpenExceptionDialog(true);
  };

  const handleCloseExceptionDialog = () => {
    setOpenExceptionDialog(false);
    setSelectedRowForException(null);
    setExceptionRemarks('');
    setExceptionAction(EXCEPTION_ACTION_DEFAULT);
  };

  const handleConfirmException = async () => {
    if (!selectedRowForException) return;

    const newAction = (exceptionAction || '').trim();
    const newRemarks = (exceptionRemarks || '').trim();
    if (!newAction) {
      showErrorMsg('Action is required');
      return;
    }
    if (!newRemarks) {
      showErrorMsg('Remarks is required');
      return;
    }

    const recordId = selectedRowForException.id;
    try {
      setExceptionPushLoading(prev => ({ ...prev, [recordId]: true }));
      const body = {
        acquirer_transaction_id: selectedRowForException.acquirer_transaction_id,
        gateway_transaction_id: selectedRowForException.gateway_transaction_id,
        transaction_datetime: selectedRowForException.acquirer_transaction_datetime,
        acquirer: selectedRowForException.acquirer,
        source: EXCEPTION_SOURCE.INCOMING_EXCEPTION,
        action: newAction,
        remarks: newRemarks,
      };

      const response = await sendDataWithPost('/payout/ingestor/push_to_exception/', body);
      if (response.status < 200 || response.status >= 300) {
        return;
      }
      try {
        const updatedDetailData = await loadDetailData(
          selectedRowForException.acquirer,
          detailType,
          selectedRowForException.acquirer_display_name,
          selectedRowForException.acquirer_transaction_currency
        );
        setSelectedDetailData(updatedDetailData);
      } catch (error) {
        console.error('Failed to refresh detail data after exception handling:', error);
      }

      showSuccessMsg('Pushed to exception handling successfully');
      setOpenExceptionDialog(false);
      setSelectedRowForException(null);
      setExceptionRemarks('');
      setExceptionAction(EXCEPTION_ACTION_DEFAULT);
    } catch (error) {
      console.error('Failed to push to exception handling:', error);
      showErrorMsg('Failed to push to exception handling');
    } finally {
      setExceptionPushLoading(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const handleRetrieveBaseRate = async (row) => {
    if (!row) {
      showErrorMsg('No row selected for base rate retrieval');
      return;
    }

    const recordId = row.id;
    setRetrieveBaseRateLoading(prev => ({ ...prev, [recordId]: true }));

    const body = {
      acquirer_transaction_id: row.acquirer_transaction_id,
      gateway_transaction_id: row.gateway_transaction_id,
      transaction_datetime: row.acquirer_transaction_datetime,
      acquirer: row.acquirer,
    };

    try {
      const response = await sendDataWithPost('/payout/ingestor/incoming_retrieve_base_rate/', body);
      if (response.status !== 200 && response.status !== 201) {
        return;
      }
      loadSummaryData();
      try {
        const updatedDetailData = await loadDetailData(
          row.acquirer,
          detailType,
          row.acquirer_display_name,
          row.acquirer_transaction_currency
        );
        setSelectedDetailData(updatedDetailData);
      } catch (error) {
        console.error('Failed to refresh detail data after base rate retrieval:', error);
      }
      showSuccessMsg('Base rate retrieval requested successfully');
    } catch (error) {
      console.error('Failed to retrieve base rate:', error);
      showErrorMsg('Failed to retrieve base rate');
    } finally {
      setRetrieveBaseRateLoading(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const handleOpenConfirmRetrieve = (row) => {
    setSelectedRowForRetrieve(row);
    setOpenConfirmRetrieveDialog(true);
  };

  const handleCloseConfirmRetrieve = () => {
    setOpenConfirmRetrieveDialog(false);
    setSelectedRowForRetrieve(null);
  };

  const handleConfirmRetrieveBaseRate = async () => {
    if (!selectedRowForRetrieve) {
      showErrorMsg('No row selected for base rate retrieval');
      return;
    }
    await handleRetrieveBaseRate(selectedRowForRetrieve);
    handleCloseConfirmRetrieve();
  };

  const paginatedData = summaryData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Incoming" isShowLink={false} />
        <Card sx={{ mb: 2, px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Box>
                <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                  Fund In Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    disableFuture
                    minDate={moment(INCOMING_PENDING_FILTER_START_DATE)}
                    inputFormat="YYYY-MM-DD"
                    value={fundInDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setFundInDate(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiFormLabel-root': {
                            lineHeight: '14px',
                            fontSize: '14px',
                          },
                          '& .MuiInputBase-input': {
                            fontSize: '14px',
                            height: '14px',
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end">
            <ButtonCustom
              sx={{ ml: 1, mt: 2, ...styles.customButton }}
              icon={<Search />}
              variant="contained"
              title="Search"
              onClick={handleSearch}
            />
            <ButtonCustom
              sx={{ ml: 1, mt: 2 }}
              icon={<Refresh />}
              title="Reset"
              onClick={handleReset}
            />
          </Stack>
        </Card>

        {/* Summary Table */}
        {loading ? (
          <CircularLoader />
        ) : (
          <Card sx={{ px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0', position: 'relative' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Acquirer</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Currency</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Sale</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Refund</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Chargeback</Typography></TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, whiteSpace: 'nowrap' }}>
                      <Typography variant="subtitle2" fontWeight={600}>Exception</Typography>
                      <CustomTooltip 
                        content="Transaction not found or amount not tally" 
                        placement="right-start" 
                        styles={{ width: "160px", fontSize: "12px", fontWeight: 400, textAlign: "center", paddingLeft: "0" }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>MDR</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>GST</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Acq Charges</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Net</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Expected Fund</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Bank Received</Typography></TableCell>
                  <TableCell align="center"><Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Remarks</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                      <Typography
                        gutterBottom
                        align="center"
                        variant="subtitle1"
                      >
                        Not found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {row.acquirer_display_name || row.acquirer}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.currency}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {renderAmountAndCount({
                          amount: row.sale_amount,
                          count: row.sale_count,
                          onClick: row.sale_count > 0 ? () => handleDetailClick(row.acquirer, 'sale', row.acquirer_display_name, row.currency) : null
                        })}
                      </TableCell>
                      <TableCell align="right">
                        {renderAmountAndCount({
                          amount: row.refund_amount,
                          count: row.refund_count,
                          onClick: row.refund_count > 0 ? () => handleDetailClick(row.acquirer, 'refund', row.acquirer_display_name, row.currency) : null
                        })}
                      </TableCell>
                      <TableCell align="right">
                        {renderAmountAndCount({
                          amount: row.chargeback_amount,
                          count: row.chargeback_count,
                          onClick: row.chargeback_count > 0 ? () => handleDetailClick(row.acquirer, 'chargeback', row.acquirer_display_name, row.currency) : null
                        })}
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 8.5 }}>
                        {renderAmountAndCount({
                          amount: row.exception_amount,
                          count: row.exception_count,
                          onClick: row.exception_count > 0 ? () => handleDetailClick(row.acquirer, 'exception', row.acquirer_display_name, row.currency) : null,
                          color: row.exception_count > 0 ? '#d32f2f' : 'inherit'
                        })}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" align="right">
                          {formatAmount(row.total_mdr)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" align="right">
                          {formatAmount(row.total_gst)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" align="right">
                          {formatAmount(row.total_acq_charges)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" align="right">
                          {formatAmount(row.total_receivable)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" align="right">
                          {formatAmount(row.total_receivable)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          align="right"
                          sx={{
                            color:
                              row.bank_received == null || row.bank_received === ''
                                ? 'text.primary'
                                : amountToCompare(row.bank_received) < amountToCompare(row.total_receivable ?? 0)
                                ? '#f44336'
                                : amountToCompare(row.bank_received) > amountToCompare(row.total_receivable ?? 0)
                                ? '#4caf50'
                                : 'text.primary'
                          }}
                        >
                          {row.bank_received != null && row.bank_received !== ''
                            ? formatAmount(row.bank_received)
                            : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                          {row.remarks ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ShowDetail text={row.remarks} limit={50} sx={{ textAlign: 'left', whiteSpace: 'nowrap' }} />
                              {hasRemarkEdit && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleEditRemark(row); }}
                                  sx={{ ...styles.customButton, minWidth: 0, px: 1.5, py: 0.5, fontSize: '12px', textTransform: 'none' }}
                                >
                                  Edit
                                </Button>
                              )}
                            </Box>
                          ) : (
                          hasRemarkAdd && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleAddRemark(row); }}
                              sx={{ ...styles.customButton, minWidth: 0, px: 1.5, py: 0.5, fontSize: '12px', textTransform: 'none' }}
                            >
                              Add
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={summaryData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          </Card>
        )}

        {/* Detail Dialog */}
        <Dialog 
          open={openDetailDialog} 
          onClose={handleCloseDetailDialog} 
          maxWidth="xl" 
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              {detailType.charAt(0).toUpperCase() + detailType.slice(1)} Details
            </Typography>
            <IconButton 
              onClick={handleCloseDetailDialog}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: COLORS.OXYGEN7_PURPLE }} />
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 1400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Acq Txn ID</Typography></TableCell>
                      {detailType === 'exception' && (
                        <TableCell><Typography variant="subtitle2" fontWeight={600}>GW Txn ID</Typography></TableCell>
                      )}
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Txn Date Time</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Currency</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Txn Amount</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>MDR Amount</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>GST Amount</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Net Amount</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Txn Type</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Acquirer</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Source</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Comparison Status</Typography></TableCell>
                      {detailType === 'exception' && (
                        <TableCell><Typography variant="subtitle2" fontWeight={600}>Action</Typography></TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedDetailData?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={detailType === 'exception' ? 13 : 11} align="center" sx={{ py: 3 }}>
                          <Typography
                            gutterBottom
                            align="center"
                            variant="subtitle1"
                          >
                            No data found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedDetailData?.map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {row.acquirer_transaction_id}
                            </Typography>
                          </TableCell>
                          {detailType === 'exception' && (
                            <TableCell>
                              <Typography variant="body2">
                                {row.gateway_transaction_id}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(row.acquirer_transaction_datetime)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.acquirer_transaction_currency}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" align="right">
                              {formatAmount(row.acquirer_transaction_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" align="right">
                              {formatAmount(row.acquirer_mdr_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" align="right">
                              {formatAmount(row.acquirer_gst_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" align="right">
                              {formatAmount(row.acquirer_net_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.gateway_transaction_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.acquirer_display_name || row.acquirer}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.source}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.comparison_status}
                            </Typography>
                          </TableCell>
                          {detailType === 'exception' && row.comparison_status && row.comparison_status !== 'Record not found' && !row.in_exception_handling && (
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {exceptionHandlingRole.includes("Add") && (
                                  <ButtonCustom
                                    sx={{
                                      ...styles.customButton,
                                      minWidth: 0,
                                      px: 2,
                                      py: 0.5,
                                      fontSize: '12px',
                                      textTransform: 'none',
                                      whiteSpace: 'nowrap',
                                    }}
                                    variant="contained"
                                    title={exceptionPushLoading[row.id]
                                      ? 'Push to Exception Handling...'
                                      : 'Push to Exception Handling'
                                    }
                                    disabled={exceptionPushLoading[row.id]}
                                    onClick={() => handleOpenExceptionDialog(row)}
                                  />
                                )}
                                {exceptionHandlingRole.includes("Add") && row.comparison_status === 'MDR not tally' && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleOpenConfirmRetrieve(row)}
                                    disabled={retrieveBaseRateLoading[row.id]}
                                    sx={{
                                      ...styles.customButton,
                                      '&:disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                        color: 'rgba(0, 0, 0, 0.26)'
                                      },
                                      fontSize: '12px',
                                      px: 2,
                                      py: 0.5,
                                      textTransform: 'none',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {retrieveBaseRateLoading[row.id] ? 'Retrieving Base Rate...' : 'Retrieve Base Rate'}
                                  </Button>
                                )}
                                {/* TODO: Waiting for payout to implement this feature
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleManualMatch(row)}
                                  disabled={manualMatchLoading[row.id]}
                                  sx={{
                                    ...styles.customButton,
                                    '&:disabled': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                      color: 'rgba(0, 0, 0, 0.26)'
                                    },
                                    fontSize: '12px',
                                    px: 2,
                                    py: 0.5
                                  }}
                                >
                                  {manualMatchLoading[row.id] ? 'Matching...' : 'Match Manually'}
                                </Button>
                                */}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
        </Dialog>

        <ManualMatchDialog
          open={openManualMatchDialog}
          onClose={handleManualMatchClose}
          onConfirm={handleManualMatchConfirm}
          loading={selectedRowForMatch ? manualMatchLoading[selectedRowForMatch.id] : false}
        />

        <RemarkDialog
          open={openRemarkDialog}
          onClose={handleRemarkDialogClose}
          row={selectedRowForRemark}
          isEdit={isEditRemark}
          onSuccess={loadSummaryData}
        />

        <ExceptionHandlingDialog
          open={openExceptionDialog}
          onClose={handleCloseExceptionDialog}
          onSubmit={handleConfirmException}
          loading={selectedRowForException ? exceptionPushLoading[selectedRowForException.id] : false}
          remarks={exceptionRemarks}
          setRemarks={setExceptionRemarks}
          action={exceptionAction}
          setAction={setExceptionAction}
        />

      <Dialog
        open={openConfirmRetrieveDialog}
        onClose={handleCloseConfirmRetrieve}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Retrieve Base Rate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to retrieve base rate for this transaction?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button
              onClick={handleCloseConfirmRetrieve}
              variant="outlined"
              size="small"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRetrieveBaseRate}
              variant="contained"
              size="small"
              sx={styles.customButton}
              disabled={selectedRowForRetrieve ? !!retrieveBaseRateLoading[selectedRowForRetrieve.id] : false}
            >
              {selectedRowForRetrieve && retrieveBaseRateLoading[selectedRowForRetrieve.id]
                ? 'Retrieving Base Rate...'
                : 'Confirm'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </Container>
  );
};

const IncomingPage = () => {
  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION)?.role || {};
  const remarkRole = userRole.remark || [];
  return <Incoming userRole={remarkRole} />;
};

export default IncomingPage;
